import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_DOUBLE_CLICK_POS_THRESHOLD, EDITOR_TOOLS, PLANE_SIDE_LABELS } from '@editor/config/constants'
import { PLANE_OPACITY, PLANE_SIDE_COLOR, PLANE_SIDE_LABEL_BG_COLOR } from '@editor/config/styles'
import { DEFAULT_PLANES_STYLES, HOVERED_PLANE_STYLES, SELECTED_PLANE_STYLES } from '@editor/config/styles/plane'
import { useEditorContext } from '@editor/contexts/Editor'
import { hashString } from '@editor/hooks/useHashUpdater'
import { PointArray } from '@editor/interfaces/attribute'
import { CanvasConfig, CanvasEventsExtra, CanvasProps } from '@editor/interfaces/editor'
import {
  InspectionItem,
  InspectionItemType,
  LineStyle,
  PolygonPlaneMeshProps,
  TransformTypes,
} from '@editor/interfaces/interfaces'
import { PlaneSide, Polygon } from '@editor/interfaces/shape'
import { AttentionTextIcon, setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import useConfigProps from '@editor/pages/projects/editor/hooks/useConfigProps'
import { useFocusItem } from '@editor/pages/projects/editor/hooks/useFocusItem'
import useUnlabeledColor from '@editor/pages/projects/editor/hooks/useUnlabeledColor'
import { isElementHovered } from '@editor/pages/projects/editor/infoPanels/utils'
import { setAllowedTransformControls } from '@editor/pages/projects/editor/store/editor'
import {
  selectIsAdjustingLabelColors,
  selectIsAdjustingUnlabeledColors,
  selectisGroupColoring,
} from '@editor/pages/projects/editor/store/selectors'
import { parseShapePartitionKey } from '@editor/services/InspectionArea'
import { toInspectionGroups } from '@editor/services/InspectionGroup'
import { findCenter } from '@editor/services/Points'
import { checkForLineIntersections, findPolygonCentroid } from '@editor/services/Polygon'
import { alignToZAxis } from '@editor/services/Shape'
import { getPlaneOpacity } from '@editor/services/Util'
import { getPlaneLabel, getVolumeEstimationItem } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectConnectedAreas, selectInspectionItems, useToolUtilSelector } from '@editor/store/selectors'
import { setIsAdjustingVertex } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import * as Sentry from '@sentry/react'
import { getContrastingColor } from '@uiw/react-color'
import { cloneDeep } from 'lodash'
import { Matrix4, Plane, Vector3 } from 'three'

import {
  DrawingStage,
  PANEL_WORKING_PLANE_LOWER_ID,
  PANEL_WORKING_PLANE_UPPER_ID,
  WORKING_PLANE_LOWER_ID,
  WORKING_PLANE_UPPER_ID,
  addWorkingPoint,
  completeBottomPlane,
  completeTopPlane,
  setDrawingStage,
  setVirtualPlaneTransformation,
  swapPlanes,
  undoLastWorkingPoint,
  updateBottomPlane,
  updateTopPlane,
} from '../store/volumeEstimation'
import { useAnchors } from './useAnchors'

/**
 * Allowed transform types when adjusting top plane.
 */
const ALLOWED_TRANSFORM_TYPES = [TransformTypes.TRANSLATE, TransformTypes.ROTATE]

/**
 * Main function for the Volume Estimation Polygon tool.
 */
export default function useMainCanvas(props?: CanvasProps): CanvasConfig {
  const { showLabels } = useConfigProps(props)
  const focusToItem = useFocusItem()

  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Context
  const { cameraRef, setPointProjectionPlane, setPointSelectionPlane } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const connectedAreas = useAppSelector(selectConnectedAreas)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const volumeItems = useMemo(() => inspectionItems.filterByType(InspectionItemType.Volume), [inspectionItems])
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const inspectionGroupsPanelItems = useAppSelector((state) => state.inspectionGroup.groups)

  const isAdjustingUnlabeledColors = useAppSelector(selectIsAdjustingUnlabeledColors)
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const isGroupColoring = useAppSelector(selectisGroupColoring)
  const volumeDisplaySettings = useAppSelector((state) => state.editorSettings.display.volume)
  const getUnlabeledColor = useUnlabeledColor()
  const currentAdjustingKey = useToolUtilSelector((state) => state.currentAdjustingKey)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const dimmedElementIds = useAppSelector((state) => state.editor.dimmedElementIds)

  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)

  const isSaving = useAppSelector((root) => root.toolVolumeEstimationPolygon.isSaving)
  const workingPoints = useAppSelector((root) => root.toolVolumeEstimationPolygon.workingPoints)
  const completedPlanes = useAppSelector((root) => root.toolVolumeEstimationPolygon.planes)
  const drawingStage = useAppSelector((root) => root.toolVolumeEstimationPolygon.drawingStage)
  const planeCreationOrder = useAppSelector((state) => state.toolVolumeEstimationPolygon.planeCreationOrder)
  const intervals = useAppSelector((state) => state.toolGrid.intervals)

  // States
  // The 'three' here refers to ThreeJS, but also coincidentally the 3 types of plane; working, upper, and lower.
  const threePlanes = useRef<Record<PlaneSide | 'working', Plane | null>>({
    working: null,
    upper: null,
    lower: null,
  })
  const [isHeightSet, setIsHeightSet] = useState(false)

  // Refs
  const currentPositionsRef = useRef<PointArray[]>([]) // only used for height selection
  const virtualPlaneTransformationRef = useRef<Matrix4 | undefined>(undefined)
  const virtualPlaneDiffTransformationRef = useRef<Matrix4 | undefined>(undefined)
  const touchedPoint = useRef<PointArray | undefined>(undefined)
  const isUpTouchedHandled = useRef(false)

  // Flags
  const inspectionGroups = toInspectionGroups(inspectionGroupsPanelItems)
  const isSelectedTool = selectedTool === EDITOR_TOOLS.VOLUME_POLYGON
  const isSelectable =
    selectedTool === EDITOR_TOOLS.MOVE || selectedTool === EDITOR_TOOLS.GRID || selectedTool === EDITOR_TOOLS.DEPTH // FIXME: this should pull from tool config
  const currentPlaneSide = planeCreationOrder[drawingStage === DrawingStage.Draw ? 0 : 1]
  const currentOtherPlaneSide = planeCreationOrder[drawingStage === DrawingStage.Draw ? 1 : 0]
  const isTopVirtual = planeCreationOrder[1] === PlaneSide.UPPER
  const isModifiable = drawingStage === DrawingStage.Complete && !isSaving

  // Hooks
  const { anchors, isClosing, setIsClosing } = useAnchors()

  /**
   * Attention messages for each drawing stage.
   */
  const attentionMessages = useMemo(
    (): Record<DrawingStage, { message: string; icon?: keyof typeof AttentionTextIcon }> => ({
      [DrawingStage.Draw]: {
        message: `${
          planeCreationOrder[0] === PlaneSide.LOWER ? '斫り面' : '仕上がり面'
        }の頂点を選択してください。最初の点を選択すると平面を作成完了します。\n(最初に作成する面は右側のパネルから選択することができます。)`,
        icon: planeCreationOrder[0] === PlaneSide.LOWER ? 'bottomPlane' : 'topPlane',
      },
      [DrawingStage.Height]: {
        message: `${
          planeCreationOrder[1] === PlaneSide.LOWER ? '斫り面' : '仕上がり面'
        }を作成する位置を選択してください。\n(作成する面は右側のパネルから選択することができます。)`,
        icon: planeCreationOrder[1] === PlaneSide.LOWER ? 'bottomPlane' : 'topPlane',
      },
      [DrawingStage.Complete]: {
        message:
          '右下の「体積を測定」をクリックしてください。\n平面を選び直す場合は右側の「検出中の要素」パネルから平面を削除してください。',
        icon: undefined,
      },
    }),
    [planeCreationOrder],
  )

  /**
   * If the plane order is changed, swap completed planes.
   */
  useEffect(() => {
    dispatch(swapPlanes())
  }, [planeCreationOrder, dispatch])

  /**
   * When workingsPoints is changed (new points added), or completedPlanes is changed (plane completed),
   * construct ThreeJS's Plane representation of them.
   *
   * This will also remove the planes during undo.
   */
  // -- Working planes (during drawing)
  useEffect(() => {
    if (workingPoints.length < 3) {
      if (drawingStage === DrawingStage.Draw) {
        threePlanes.current.working = null
        setPointProjectionPlane(null)
        setPointSelectionPlane(null)
      }
      return
    }

    threePlanes.current.working = new Plane().setFromCoplanarPoints(
      new Vector3(...workingPoints[0]),
      new Vector3(...workingPoints[1]),
      new Vector3(...workingPoints[2]),
    )

    if (drawingStage === DrawingStage.Draw) setPointProjectionPlane(threePlanes.current.working)
  }, [workingPoints, drawingStage, setPointProjectionPlane, setPointSelectionPlane])

  // -- Bottom plane
  useEffect(() => {
    if (!completedPlanes.lower || completedPlanes.lower.points.length < 3) {
      threePlanes.current.lower = null
      setIsHeightSet(false)
      return
    }

    threePlanes.current.lower = new Plane().setFromCoplanarPoints(
      new Vector3(...completedPlanes.lower.points[0]),
      new Vector3(...completedPlanes.lower.points[1]),
      new Vector3(...completedPlanes.lower.points[2]),
    )
    threePlanes.current.working = null
  }, [completedPlanes.lower])

  // -- Upper plane.
  //    Note that it can either be virtual (freedom of adjustments) or constrained by PCD.
  useEffect(() => {
    if (!completedPlanes.upper || completedPlanes.upper.points.length < 3) {
      threePlanes.current.upper = null
      virtualPlaneTransformationRef.current = undefined
      virtualPlaneDiffTransformationRef.current = undefined
      dispatch(setAllowedTransformControls([]))
      setIsHeightSet(false)
      return
    }

    // If upper plane is virtual, use the diff of transformation matrix and polygon's world matrix
    const pointsV3 = completedPlanes.upper.points.map((p) =>
      new Vector3(...p).applyMatrix4(virtualPlaneDiffTransformationRef.current || new Matrix4()),
    )

    threePlanes.current.upper = new Plane().setFromCoplanarPoints(pointsV3[0], pointsV3[1], pointsV3[2])
    threePlanes.current.working = null
  }, [completedPlanes.upper, setPointProjectionPlane, dispatch])

  /**
   * Set attention text based on the current drawing stage.
   */
  useEffect(() => {
    if (!isSelectedTool) {
      // Clear attention text when tool is deselected
      dispatch(setAttentionText({ message: '' }))
      return
    }

    if (attentionMessages[drawingStage]) {
      dispatch(setAttentionText(attentionMessages[drawingStage]))
    } else {
      dispatch(setAttentionText({ message: '' }))
    }
  }, [drawingStage, isSelectedTool, attentionMessages, dispatch])

  /**
   * Validate if adding a new point will create intersecting lines.
   *
   * @param points Current points including the new point to be added
   */
  const validateIntersectingLines = useCallback((points: PointArray[]): boolean => {
    // Need at least 3 points to have intersecting lines
    if (points.length < 3) return true

    // Check for line intersections in 2D (since points are coplanar)
    const { points: zAlignedPoints } = alignToZAxis(points.map((p) => new Vector3(...p)))
    if (checkForLineIntersections(zAlignedPoints)) {
      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = toaster.create({
        title: '交差検出',
        description: '新しい点により線分が交差してしまいます。別の位置を選択してください。',
        type: 'warning',
        duration: 5000,
      })
      return false
    }

    return true
  }, [])

  /**
   * End drawing the polygon.
   */
  const endDrawing = useCallback(
    ({ isDoubleClicked }: CanvasEventsExtra) => {
      // Check for line intersections in 2D (since points are coplanar)
      if (!validateIntersectingLines(workingPoints)) {
        return
      }

      dispatch(
        (currentPlaneSide === PlaneSide.UPPER ? completeTopPlane : completeBottomPlane)({
          isDoubleClicked,
        }),
      )
      dispatch(setDrawingStage(DrawingStage.Height))
      setIsClosing(false)
      setPointProjectionPlane(null)
      setPointSelectionPlane(null)
    },
    [
      currentPlaneSide,
      workingPoints,
      setIsClosing,
      setPointProjectionPlane,
      setPointSelectionPlane,
      validateIntersectingLines,
      dispatch,
    ],
  )

  /**
   * Event Handlers (onUp).
   * Will be called by both mouse and touch events.
   */
  const handleUp = useCallback(
    (
      me: React.MouseEvent<HTMLDivElement> | null,
      te: React.TouchEvent<HTMLDivElement> | null,
      point: PointArray | undefined,
      solidMeshPoint: PointArray | undefined,
      extras: CanvasEventsExtra,
    ) => {
      const { isDoubleClicked, isDragged } = extras
      const wasTouched = !!te
      const clickedPoint = solidMeshPoint || point
      if (isDragged || !isSelectedTool || !clickedPoint || drawingStage === DrawingStage.Complete) return

      // -- Height Selection Stage
      if (drawingStage === DrawingStage.Height) {
        // if touch device was used, need to to use move `currentPositionsRef` according to `clickedPoint`
        // since only interactions with mouse will trigger the plane adjustment.
        if (wasTouched) {
          const plane = threePlanes.current[currentOtherPlaneSide]
          if (!plane) return

          // Adjust plane to new position and project current positions onto it
          threePlanes.current[currentPlaneSide] = plane.clone()
          threePlanes.current[currentPlaneSide].constant = -threePlanes.current[currentPlaneSide].normal.dot(
            new Vector3(...clickedPoint),
          )
          currentPositionsRef.current = currentPositionsRef.current.map((pt) =>
            threePlanes.current[currentPlaneSide]!.projectPoint(new Vector3(...pt), new Vector3()).toArray(),
          )
        }

        if (currentPositionsRef.current.length >= 3) {
          dispatch(
            (currentPlaneSide === PlaneSide.UPPER ? updateTopPlane : updateBottomPlane)(currentPositionsRef.current),
          )
          dispatch(setDrawingStage(DrawingStage.Complete))
        }
        setIsHeightSet(true)
        return
      }

      // -- Drawing Stage
      if (drawingStage === DrawingStage.Draw) {
        // Figure out distance in screen-space between clicked point and first working point
        // Click point we can get from event but first working point we need to project to screen space
        const ev = me || te
        let isScreenSpaceClose = false
        if (workingPoints.length > 2 && ev) {
          const screenPoint = me
            ? { x: me.clientX, y: me.clientY }
            : { x: te!.changedTouches[0].clientX, y: te!.changedTouches[0].clientY }

          const canvasRect = (ev.target as HTMLDivElement).getBoundingClientRect()
          const closingPointNDC = new Vector3(...workingPoints[0]).project(cameraRef.current!)
          const closingPointScreen = {
            x: ((closingPointNDC.x + 1) / 2) * canvasRect.width + canvasRect.left,
            y: ((-closingPointNDC.y + 1) / 2) * canvasRect.height + canvasRect.top,
          }
          const distanceToFirstPoint = Math.hypot(
            screenPoint.x - closingPointScreen.x,
            screenPoint.y - closingPointScreen.y,
          )
          isScreenSpaceClose = distanceToFirstPoint < EDITOR_DOUBLE_CLICK_POS_THRESHOLD
        }

        // It's closing if:
        // 1. user is in closing mode
        // 2. working points are more than 2 and user double clicked
        // 3. user clicked near the first point in screen space
        const completesPolygon = isClosing || isScreenSpaceClose || (workingPoints.length >= 2 && isDoubleClicked)
        if (completesPolygon) {
          endDrawing(extras)
        } else if (!isDoubleClicked) {
          dispatch(addWorkingPoint(clickedPoint))
          currentPositionsRef.current = [...workingPoints, clickedPoint]

          // Once we have 3 points, we can start projecting the point selection plane
          if (currentPositionsRef.current.length === 3) {
            setPointSelectionPlane(
              new Plane().setFromCoplanarPoints(
                new Vector3(...currentPositionsRef.current[0]),
                new Vector3(...currentPositionsRef.current[1]),
                new Vector3(...currentPositionsRef.current[2]),
              ),
            )
          }
        }
      }
    },
    [
      isSelectedTool,
      currentPlaneSide,
      currentOtherPlaneSide,
      drawingStage,
      workingPoints,
      cameraRef,
      isClosing,
      endDrawing,
      dispatch,
    ],
  )

  /**
   * Focus on a plane.
   *
   * @param hashedItemId Hashed ID of the inspection item to focus on. Use the hashString function to generate this from the item ID.
   */
  const onFocusItem = useCallback(
    async (hashedItemId: string) => {
      try {
        // Get the inspection item and make sure it's a plane item
        const item = volumeItems.find((it) => hashString(it.inspection_item_id!) === hashedItemId)
        if (!item) return // no need to throw error, it can be a valid call from URL deep-link broadcasted to all tools

        // Get the shape associated with the inspection item
        const shape = shapes?.polygons?.find(
          (polygon) => item.shape_ids.polygons?.includes(polygon.shape_id) && polygon.plane_side === PlaneSide.UPPER,
        )
        if (!shape) throw new Error('Shape not found for the inspection item')

        await focusToItem(shape.positions, { customProfile: item.camera_profile || undefined })
      } catch (error) {
        Sentry.captureException(error, { tags: { location: 'VolumeDetection/useMainCanvas/onFocusItem' } })
      }
    },
    [volumeItems, shapes, focusToItem],
  )

  /**
   * Generate props for upper plane mesh.
   *
   * This can change depending on whether the upper plane is virtual or not.
   */
  const commonUpperPlaneProps = useMemo(() => {
    const isSelectingBottom = drawingStage === DrawingStage.Height && planeCreationOrder[0] === PlaneSide.UPPER

    return {
      id: WORKING_PLANE_UPPER_ID,
      polygon: {
        shape_id: WORKING_PLANE_UPPER_ID,
        transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
      } as Polygon,
      label: {
        visible: !isSelectingBottom,
        label: PLANE_SIDE_LABELS.upper,
        styles: {
          color: getContrastingColor(volumeDisplaySettings.upper.color),
          backgroundColor: volumeDisplaySettings.upper.color,
        },
      },
      color: isGroupColoring ? PLANE_SIDE_COLOR.default : volumeDisplaySettings.upper.color,
      opacity: isSelectingBottom ? PLANE_OPACITY.DIMMED : volumeDisplaySettings.upper.opacity,
      selectable: false,
      guideLine: {
        style: LineStyle.Dashed,
      },
      anchorProps: {
        color: PLANE_SIDE_COLOR.default,
      },
      guideLabel: {
        visible: !isSelectingBottom,
        color: volumeDisplaySettings.upper.edge.label.text_color,
        backgroundColor: volumeDisplaySettings.upper.edge.label.bg_color,
      },
      vertexAnchors: {
        visible: isModifiable,
        depthTest: false,
        depthWrite: false,
        renderOrder: 999,
      },
      isOpenLoop: true,
      isEditing: editingElementId === WORKING_PLANE_UPPER_ID,
      depthTest: false,
      depthWrite: false,
    }
  }, [editingElementId, isModifiable, drawingStage, planeCreationOrder, isGroupColoring, volumeDisplaySettings])

  return {
    objects: {
      circleAnchors: anchors,
      polygonPlaneMeshesV2: [
        // Saved planes from inspection items
        ...useMemo(
          () =>
            (
              shapes?.polygons
                .map((polygon) => ({
                  polygon,
                  volume: getVolumeEstimationItem(inspectionItems.items, polygon.shape_id),
                }))
                .filter(({ volume }) => volume) as { polygon: Polygon; volume: InspectionItem }[]
            ).map<PolygonPlaneMeshProps>(({ polygon, volume: volumeItem }) => {
              const isHovered =
                isElementHovered(volumeItem, hoveredElementId, inspectionGroups) ||
                isElementHovered(polygon, hoveredElementId, inspectionGroups)
              const isSelected =
                selectedElementIds.includes(polygon.shape_id) ||
                selectedElementIds.includes(volumeItem.inspection_item_id!)
              const isHidden =
                (hiddenElementIds.includes(polygon.shape_id) ||
                  hiddenElementIds.includes(volumeItem.inspection_item_id!)) &&
                !isHovered

              const group = inspectionGroups.find((grp) => volumeItem.inspection_group_id === grp.group_id)
              const label =
                polygon.plane_side === PlaneSide.UPPER ? getPlaneLabel(polygon, inspectionItems.items) : undefined
              const ids = parseShapePartitionKey(polygon.partition_key || '')
              const parentArea = connectedAreas?.find(
                (area) => area.current.area.inspection_area_id === ids.inspectionAreaId,
              )

              let styles: Partial<PolygonPlaneMeshProps> = cloneDeep(DEFAULT_PLANES_STYLES)

              if (isHovered) {
                styles = {
                  ...styles,
                  ...HOVERED_PLANE_STYLES,
                  guideLabel: {},
                  label: {
                    ...HOVERED_PLANE_STYLES.label,
                    ...label,
                  },
                }
              } else if (isSelected) {
                styles = {
                  ...styles,
                  ...SELECTED_PLANE_STYLES,
                  guideLabel: isHidden ? undefined : {},
                  label: {
                    ...SELECTED_PLANE_STYLES.label,
                    ...label,
                  },
                }
              }

              // If labels are disabled, remove label styles
              if (volumeDisplaySettings.upper.primary.label.visible === false) {
                delete styles.label
              }

              // If there's going to be a label, set the colors
              if (styles.label) {
                styles.label = {
                  ...styles.label,
                  styles: {
                    backgroundColor: volumeDisplaySettings.upper.primary.label.bg_color,
                    color: volumeDisplaySettings.upper.primary.label.text_color,
                  },
                  isAdjustingColors:
                    isAdjustingLabelColors &&
                    currentAdjustingKey !== undefined &&
                    ['volume.upper.primary.label.text_color', 'volume.upper.primary.label.bg_color'].includes(
                      currentAdjustingKey,
                    ),
                }
              }

              // If guide label are disabled, remove guide label styles
              if (volumeDisplaySettings[polygon.plane_side!].edge.label.visible === false) {
                delete styles.guideLabel
              }

              // If there's going to guide label, customize the styles a bit
              if (styles.guideLabel) {
                styles.guideLabel = {
                  ...styles.guideLabel,
                  styles: {
                    backgroundColor:
                      volumeDisplaySettings[polygon.plane_side!]?.edge.label.bg_color ||
                      PLANE_SIDE_LABEL_BG_COLOR[polygon.plane_side!],
                    color: volumeDisplaySettings[polygon.plane_side!]?.edge.label.text_color || '#fff',
                  },
                  isAdjustingColors:
                    isAdjustingLabelColors &&
                    currentAdjustingKey !== undefined &&
                    [
                      `volume.${polygon.plane_side!}.edge.label.text_color`,
                      `volume.${polygon.plane_side!}.edge.label.bg_color`,
                    ].includes(currentAdjustingKey),
                }
              }

              // Force hide label if we're doing grid on the same volume to lessen the clutter
              if (styles.guideLine) {
                Object.values(intervals).forEach((interval) => {
                  if (polygon.shape_id === interval.topPlaneId || polygon.shape_id === interval.bottomPlaneId) {
                    styles.guideLabel = undefined
                    styles.label = undefined
                  }
                })
              }

              // If user is on the Rebar detection tool, dim the upper plane
              if (selectedTool === EDITOR_TOOLS.CYLINDER && polygon.plane_side === PlaneSide.UPPER) {
                styles.opacity = PLANE_OPACITY.DIMMED
              } else {
                styles.opacity =
                  polygon.plane_side === PlaneSide.UPPER
                    ? getPlaneOpacity(polygon, dimmedElementIds, isHovered, PLANE_OPACITY)
                    : 1
              }

              return {
                ...styles,
                id: volumeItem.inspection_item_id!,
                name: `${volumeItem.part_name || volumeItem.inspection_item_id}_${polygon.plane_side === PlaneSide.UPPER ? 'top' : 'bottom'}`,
                polygon,
                offsetTransformation: parentArea?.matrix,
                color: isGroupColoring
                  ? group?.color || getUnlabeledColor(ids.inspectionAreaId)
                  : volumeDisplaySettings[polygon.plane_side!].color,
                isAdjustingColors:
                  (isAdjustingUnlabeledColors && isGroupColoring) ||
                  (!isGroupColoring &&
                    currentAdjustingKey !== undefined &&
                    [`volume.${polygon.plane_side!}.color`, `volume.${polygon.plane_side!}.color`].includes(
                      currentAdjustingKey,
                    )),
                isAdjustingLabelColors,
                selectable: isSelectable,
                opacity: isHidden ? 0 : volumeDisplaySettings.upper.opacity,
                guideOpacity: isHidden ? 0 : styles.guideOpacity,
                label: showLabels && !isHidden && label ? styles.label : undefined,
                guideLabel: showLabels && !isHidden ? styles.guideLabel : undefined,
                isOpenLoop: true,
              }
            }) || [],
          [
            showLabels,
            inspectionItems,
            inspectionGroups,
            intervals,
            shapes,
            hoveredElementId,
            selectedElementIds,
            dimmedElementIds,
            isSelectable,
            isGroupColoring,
            isAdjustingUnlabeledColors,
            isAdjustingLabelColors,
            selectedTool,
            hiddenElementIds,
            connectedAreas,
            currentAdjustingKey,
            volumeDisplaySettings,
            getUnlabeledColor,
          ],
        ),

        // Working plane
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!workingPoints.length) return []

          const isHeightSelection = drawingStage === DrawingStage.Height
          const originalPoints = completedPlanes[currentOtherPlaneSide]

          return [
            {
              id: 'working-volume-polygon-drawing',
              polygon: {
                shape_id: 'working-volume-polygon-drawing',
                positions: workingPoints,
                vertices: workingPoints.map((v) => [v[0], v[1]] as [number, number]),
                center: findCenter(workingPoints.map((v) => new Vector3(...v)))?.toArray(),
                transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
              } as Polygon,
              transparent: true,
              color: isGroupColoring
                ? PLANE_SIDE_COLOR[currentPlaneSide]
                : volumeDisplaySettings[currentPlaneSide].color,
              opacity: volumeDisplaySettings.upper.opacity,
              label: {
                label: PLANE_SIDE_LABELS[currentPlaneSide],
                styles: {
                  color: getContrastingColor(volumeDisplaySettings[currentPlaneSide].color),
                  backgroundColor: volumeDisplaySettings[currentPlaneSide].color,
                },
              },
              guideLine: {
                color: isClosing ? 'red' : '#ffffff',
                style: LineStyle.Dashed,
              },
              guideLabel: {
                color: volumeDisplaySettings[currentPlaneSide].edge.label.text_color,
                backgroundColor: volumeDisplaySettings[currentPlaneSide].edge.label.bg_color,
                hideLastLabel: isClosing,
              },
              visible: (drawingStage === DrawingStage.Height && isHeightSet) || drawingStage !== DrawingStage.Height,
              selectable: false,
              isOpenLoop: true,
              isWorking: true,
              // For height selection, we'll manually define the points since it's about adjusting height of the other plane.
              getPoints: isHeightSelection
                ? (curPos: PointArray) => {
                    // For height selection, we use the current point as reference to project the working points onto the plane
                    const plane = threePlanes.current[currentOtherPlaneSide]
                    if (!plane) return workingPoints

                    currentPositionsRef.current = (originalPoints?.points || workingPoints).map((pt) => {
                      // adjust projected point according to current position's height difference
                      const distanceToCurrent = plane.distanceToPoint(new Vector3(...curPos))
                      return [
                        pt[0] + plane.normal.x * distanceToCurrent,
                        pt[1] + plane.normal.y * distanceToCurrent,
                        pt[2] + plane.normal.z * distanceToCurrent,
                      ]
                    })

                    return currentPositionsRef.current
                  }
                : undefined,
            },
          ]
        }, [
          workingPoints,
          completedPlanes,
          currentPlaneSide,
          currentOtherPlaneSide,
          volumeDisplaySettings,
          isGroupColoring,
          isClosing,
          isHeightSet,
          drawingStage,
        ]),

        // Completed planes (bottom)
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!completedPlanes.lower || hiddenElementIds.includes(PANEL_WORKING_PLANE_LOWER_ID)) return []

          const pointsV3 = completedPlanes.lower.points.map((p) => new Vector3(...p))
          const centroid = findPolygonCentroid(pointsV3)

          return [
            {
              id: WORKING_PLANE_LOWER_ID,
              polygon: {
                shape_id: WORKING_PLANE_LOWER_ID,
                positions: completedPlanes.lower.points,
                vertices: completedPlanes.lower.points.map((v) => [v[0], v[1]] as [number, number]),
                center: centroid.toArray(),
                transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
              } as Polygon,
              color: isGroupColoring ? PLANE_SIDE_COLOR.lower : volumeDisplaySettings.lower.color,
              opacity: volumeDisplaySettings.upper.opacity,
              label: {
                label: PLANE_SIDE_LABELS.lower,
                styles: {
                  color: getContrastingColor(volumeDisplaySettings.lower.color),
                  backgroundColor: volumeDisplaySettings.lower.color,
                },
              },
              guideLine: {
                style: LineStyle.Dashed,
              },
              anchorProps: {
                color: PLANE_SIDE_COLOR.lower,
              },
              guideLabel: {
                styles: {
                  color: volumeDisplaySettings.lower.edge.label.text_color,
                  backgroundColor: volumeDisplaySettings.lower.edge.label.bg_color,
                },
              },
              vertexAnchors: {
                visible: drawingStage === DrawingStage.Complete && !isSaving,
                depthTest: false,
                depthWrite: false,
                renderOrder: 999,
                onDown: () => {
                  setPointSelectionPlane(threePlanes.current.lower)
                },
                onUpdated:
                  drawingStage === DrawingStage.Complete
                    ? (points: PointArray[]) => {
                        // Check for line intersections in 2D (since points are coplanar)
                        if (!validateIntersectingLines(points)) {
                          // restore previous points
                          dispatch(updateBottomPlane([...completedPlanes.lower!.points]))
                          return
                        }

                        dispatch(updateBottomPlane(points))
                        setPointSelectionPlane(null)
                      }
                    : undefined,
              },
              transparent: true,
              selectable: false,
              isOpenLoop: true,
              isEditing: editingElementId === WORKING_PLANE_LOWER_ID,
              depthTest: false,
              depthWrite: false,
            },
          ]
        }, [
          isSaving,
          completedPlanes.lower,
          hiddenElementIds,
          editingElementId,
          drawingStage,
          isGroupColoring,
          volumeDisplaySettings,
          validateIntersectingLines,
          setPointSelectionPlane,
          dispatch,
        ]),

        // Completed planes (top). Only use this if order is upper -> lower.
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!completedPlanes.upper || hiddenElementIds.includes(PANEL_WORKING_PLANE_UPPER_ID) || isTopVirtual)
            return []

          const pointsV3 = completedPlanes.upper.points.map((p) => new Vector3(...p))
          const centroid = findPolygonCentroid(pointsV3)

          // Props to be added when the mesh is modifiable
          const modifiableProps: Partial<PolygonPlaneMeshProps> = isModifiable
            ? {
                vertexAnchors: {
                  onDown: () => {
                    setPointSelectionPlane(threePlanes.current.upper)
                  },
                },
              }
            : {}

          return [
            {
              ...commonUpperPlaneProps,
              ...modifiableProps,
              polygon: {
                ...commonUpperPlaneProps.polygon,
                positions: completedPlanes.upper.points,
                vertices: completedPlanes.upper.points.map((v) => [v[0], v[1]] as [number, number]),
                center: centroid.toArray(),
              } as Polygon,
              vertexAnchors: {
                ...commonUpperPlaneProps.vertexAnchors,
                ...modifiableProps.vertexAnchors,
                onDown: () => {
                  setPointSelectionPlane(threePlanes.current.upper)
                },
                onUpdated: isModifiable
                  ? (points: PointArray[]) => {
                      // Check for line intersections in 2D (since points are coplanar)
                      if (!validateIntersectingLines(points)) {
                        // restore previous points
                        dispatch(updateTopPlane([...completedPlanes.upper!.points]))
                        return
                      }

                      dispatch(updateTopPlane(points))
                      setPointSelectionPlane(null)
                    }
                  : undefined,
              },
            },
          ]
        }, [
          commonUpperPlaneProps,
          completedPlanes.upper,
          hiddenElementIds,
          isTopVirtual,
          isModifiable,
          setPointSelectionPlane,
          validateIntersectingLines,
          dispatch,
        ]),
      ],
      polygonPlaneMeshTransformable: [
        // Completed planes (top). Only use this if order is lower -> upper.
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!completedPlanes.upper || hiddenElementIds.includes(PANEL_WORKING_PLANE_UPPER_ID) || !isTopVirtual)
            return []
          const pointsV3 = completedPlanes.upper.points.map((p) => new Vector3(...p))
          const centroid = new Vector3(...completedPlanes.upper.originalCentroid)
          const matrix = new Matrix4().setPosition(centroid)
          const worldMatrix = matrix.clone()
          const worldMatrixInverse = worldMatrix.clone().invert()
          const localPoints = pointsV3.map((p) => p.clone().applyMatrix4(worldMatrixInverse))

          // Props to be added when the mesh is modifiable
          const modifiableProps: Partial<PolygonPlaneMeshProps> = isModifiable
            ? {
                vertexAnchors: {
                  onDown() {
                    setPointSelectionPlane(threePlanes.current.upper)
                    dispatch(setIsAdjustingVertex(true))
                  },
                  // -- vertex modification completed
                  onUpdated(localPts: PointArray[]) {
                    dispatch(setIsAdjustingVertex(false))

                    // Transform local points back to world points
                    const tempVector = new Vector3()
                    const worldPoints = localPts.map((p) =>
                      tempVector
                        .set(...p)
                        .applyMatrix4(worldMatrix)
                        .toArray(),
                    )

                    // Check for line intersections in 2D (since points are coplanar)
                    if (!validateIntersectingLines(worldPoints)) {
                      // restore previous points
                      dispatch(updateTopPlane([...completedPlanes.upper!.points]))
                      return
                    }

                    dispatch(updateTopPlane(worldPoints))
                    setPointSelectionPlane(null)
                  },
                },
                // -- whole plane transformation completed
                onUpdated(localPositions: PointArray[], latestMatrix: Matrix4) {
                  // keep track of latest matrix and its diff to plane's world matrix for later usage
                  dispatch(setVirtualPlaneTransformation(latestMatrix.toArray()))
                  virtualPlaneTransformationRef.current = latestMatrix
                  virtualPlaneDiffTransformationRef.current = latestMatrix
                    .clone()
                    .multiply(worldMatrix.clone().invert())

                  // update plane using latest matrix
                  const worldPoints = localPositions.map((p) => new Vector3(...p).applyMatrix4(latestMatrix))
                  threePlanes.current.upper = new Plane().setFromCoplanarPoints(
                    worldPoints[0],
                    worldPoints[1],
                    worldPoints[2],
                  )
                },
                onReset() {
                  // reset transformation refs
                  virtualPlaneTransformationRef.current = undefined
                  virtualPlaneDiffTransformationRef.current = undefined
                  dispatch(setVirtualPlaneTransformation(undefined))
                  dispatch(updateTopPlane([...completedPlanes.upper!.originalPoints]))
                },
              }
            : {}

          if (virtualPlaneTransformationRef.current) {
            matrix.copy(virtualPlaneTransformationRef.current)
          }

          return [
            {
              ...commonUpperPlaneProps,
              ...modifiableProps,
              polygon: {
                ...commonUpperPlaneProps.polygon,
                positions: localPoints.map((v) => v.toArray()),
                vertices: localPoints.map((v) => [v.x, v.y] as [number, number]),
                center: centroid.toArray(),
                transformation: matrix.toArray(),
              } as Polygon,
              offsetTransformation: matrix,
              vertexAnchors: {
                ...commonUpperPlaneProps.vertexAnchors,
                ...modifiableProps.vertexAnchors,
              },
              transformControls: {
                enabled: !isSaving,
                allowedTransforms: ALLOWED_TRANSFORM_TYPES,
              },
            },
          ]
        }, [
          commonUpperPlaneProps,
          completedPlanes.upper,
          hiddenElementIds,
          isTopVirtual,
          isModifiable,
          isSaving,
          setPointSelectionPlane,
          validateIntersectingLines,
          dispatch,
        ]),
      ],
    },
    events: {
      onTouchStart(ev, point, solidMeshPoint) {
        touchedPoint.current = point || solidMeshPoint
      },
      onTouchEnd(ev, extras) {
        handleUp(null, ev, touchedPoint.current, touchedPoint.current, extras)
        isUpTouchedHandled.current = true
        touchedPoint.current = undefined
      },
      onMouseUp(ev, point, solidMeshPoint, extras) {
        if (isUpTouchedHandled.current) {
          isUpTouchedHandled.current = false
          return
        }

        handleUp(ev, null, point, solidMeshPoint, extras)
      },
      onUndo() {
        dispatch(undoLastWorkingPoint())
      },
      onComplete() {
        if (drawingStage === DrawingStage.Draw && workingPoints.length >= 3) {
          endDrawing({ isDoubleClicked: false, isTripleClicked: false, isDragged: false })
        }
      },
      onMove() {
        if (drawingStage === DrawingStage.Height && !touchedPoint.current) setIsHeightSet(true)
      },
      onFocusItem,
    },
  }
}
